import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import GoogleLogo from "../../media/g-logo.png";
import { CredentialResponse, GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { useAppSelector } from "store";
import { selectFrontendParams } from "store/general/general-slice-selectors";

const GoogleLoginInput: React.FC<{ onSuccess: (response: CredentialResponse) => void; onError: () => void }> = ({
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const frontendParams = useAppSelector((state) => selectFrontendParams(state));
  const googleClientId = frontendParams.data?.googleClientId;

  const [googleAuthReady, setGoogleAuthReady] = useState(false);
  const googleAuthInitializing = useRef(false);

  useEffect(() => {
    if (
      !Capacitor.isNativePlatform() ||
      googleAuthReady ||
      googleAuthInitializing.current ||
      googleClientId == null
    ) {
      return;
    }

    googleAuthInitializing.current = true;
    SocialLogin.initialize({ google: { webClientId: googleClientId } })
      .then(() => setGoogleAuthReady(true))
      .catch((error) => {
        console.error("Failed to initialize Google social login", error);
      })
      .finally(() => {
        googleAuthInitializing.current = false;
      });
  }, [googleClientId, googleAuthReady]);

  const nativeLoginClickHandler = async () => {
    try {
      const response = await SocialLogin.login({ provider: "google", options: {} });
      const result = response.result;
      const idToken = result.responseType === "online" ? result.idToken : undefined;
      if (idToken != null) {
        onSuccess({ credential: idToken });
      } else {
        onError();
      }
    } catch (error) {
      onError();
    }
  };

  if (Capacitor.isNativePlatform()) {
    return (
      <Button
        onClick={nativeLoginClickHandler}
        disabled={!googleAuthReady}
        variant="contained"
        size="large"
        color="inherit"
        sx={{ p: 1.5, px: 2, justifyContent: "start", backgroundColor: "#fff" }}
        startIcon={<img src={GoogleLogo} alt="" style={{ width: "24px" }} />}
      >
        {t("button-login-with-google")}
      </Button>
    );
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId ?? ""}>
      <GoogleLogin onSuccess={onSuccess} onError={onError} />
    </GoogleOAuthProvider>
  );
};

export default GoogleLoginInput;
